import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Chip, Divider, TextField, Typography
} from '@mui/material';

import {Grid } from "@mui/material"
import FormDialog from '../../common/FormDialog';
import { reportCardAPI } from '../../../services/api';

const termLabel = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const studName  = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const subjName  = (s) => s?.subject_name || s?.name || '';

const SectionLabel = ({ children }) => (
  <Typography variant="caption" fontWeight={700} color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

const SubjectCommentFormDialog = ({
  open, onClose, onSubmit, editingRecord,
  formData, setFormData, saving, isMobile = false,
  students = [], terms = [], subjects = [], classStreams = [],
}) => {
  const [templates,        setTemplates]        = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [classFilter,      setClassFilter]      = useState(null);
  const [streamFilter,     setStreamFilter]     = useState(null);
  const [classSubjects,    setClassSubjects]    = useState(null); // null = use all subjects

  useEffect(() => {
    if (!open) { setSelectedTemplate(null); setClassFilter(null); setStreamFilter(null); setClassSubjects(null); return; }
    reportCardAPI.getCommentTemplates({ comment_type: 'Subject' })
      .then((res) => setTemplates(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => setTemplates([]));
  }, [open]);

  // fetch subjects for selected class/stream
  useEffect(() => {
    if (!classFilter) { setClassSubjects(null); return; }
    const cs = classStreams.find((c) =>
      c.class_id === classFilter.id && (!streamFilter || c.stream_id === streamFilter.id)
    );
    if (!cs) { setClassSubjects(null); return; }
    reportCardAPI.getSubjectPapersForClass({ class_id: cs.class_id, stream_id: cs.stream_id })
      .then((res) => {
        const papers = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const seen = new Map();
        papers.forEach((p) => {
          const s = p.subject_paper?.subject || p.subject;
          if (s?.id && !seen.has(s.id)) seen.set(s.id, s);
        });
        const list = Array.from(seen.values());
        setClassSubjects(list.length > 0 ? list : null);
        // clear subject if it's not in the new list
        if (formData.subject && list.length > 0 && !list.find((s) => s.id === formData.subject.id)) {
          setFormData((prev) => ({ ...prev, subject: null }));
        }
      })
      .catch(() => setClassSubjects(null));
  }, [classFilter, streamFilter]); // eslint-disable-line

  useEffect(() => {
    if (!open || !editingRecord?.student) return;
    const cs = classStreams.find((c) => c.id === editingRecord.student.class_stream_id);
    if (!cs) return;
    const className = cs.name?.split('-')[0]?.trim() || '';
    setClassFilter({ id: cs.class_id, name: className });
    setStreamFilter({ id: cs.stream_id, name: cs.stream_name || '' });
  }, [open, editingRecord, classStreams]);

  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id)) {
        const className = cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}`;
        seen.set(cs.class_id, { id: cs.class_id, name: className });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  const streams = useMemo(() => {
    const pool = classFilter ? classStreams.filter((cs) => cs.class_id === classFilter.id) : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id))
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  const filteredStudents = useMemo(() => {
    if (!classFilter && !streamFilter) return students;
    const csIds = new Set(
      classStreams
        .filter((cs) => (!classFilter || cs.class_id === classFilter.id) && (!streamFilter || cs.stream_id === streamFilter.id))
        .map((cs) => cs.id)
    );
    return students.filter((s) => csIds.has(s.class_stream_id));
  }, [students, classStreams, classFilter, streamFilter]);

  const gradeLabel = (t) => {
    const b = t.grade_boundary;
    return b ? `${t.template_code} — ${b.grade} (${b.min_percentage}–${b.max_percentage}%)` : t.template_code;
  };

  const handleClassChange = (_, v) => {
    setClassFilter(v); setStreamFilter(null);
    if (formData.student) setFormData({ ...formData, student: null });
  };
  const handleStreamChange = (_, v) => {
    setStreamFilter(v);
    if (formData.student) setFormData({ ...formData, student: null });
  };

  return (
    <FormDialog
      open={open} onClose={onClose} onSubmit={onSubmit}
      title={editingRecord ? 'Edit Subject Comment' : 'Add Subject Comment'}
      saving={saving} isMobile={isMobile} maxWidth="md"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

        {/* ── 1. Select Student ── */}
        <Box>
          <SectionLabel>1. Select Student</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={classes} value={classFilter} onChange={handleClassChange}
                getOptionLabel={(c) => c.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={streams} value={streamFilter} onChange={handleStreamChange}
                getOptionLabel={(s) => s.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete fullWidth options={filteredStudents} value={formData.student}
                onChange={(_, v) => setFormData({ ...formData, student: v })}
                getOptionLabel={studName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student *" size="small"
                  helperText={!classFilter ? 'Select a class first to narrow the list' : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''} in this class/stream`}
                />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 2. Term & Subject ── */}
        <Box>
          <SectionLabel>2. Term &amp; Subject</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete fullWidth options={terms} value={formData.term}
                onChange={(_, v) => setFormData({ ...formData, term: v })}
                getOptionLabel={termLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {t.is_current && <Chip label="Current" size="small" color="success" />}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term *" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete fullWidth options={classSubjects ?? subjects} value={formData.subject}
                onChange={(_, v) => setFormData({ ...formData, subject: v })}
                getOptionLabel={subjName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Subject *" size="small"
                  helperText={classSubjects ? `${classSubjects.length} subject${classSubjects.length !== 1 ? 's' : ''} in this class` : undefined}
                />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 3. Comment ── */}
        <Box>
          <SectionLabel>3. Comment</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {templates.length > 0 && (
              <Autocomplete fullWidth options={templates} value={selectedTemplate}
                onChange={(_, t) => { setSelectedTemplate(t); if (t) setFormData({ ...formData, comment_text: t.comment_text }); }}
                getOptionLabel={gradeLabel} isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label="Pick from template (optional)" size="small" helperText="Fills the comment box below" />}
              />
            )}
            <TextField fullWidth multiline rows={4} label="Comment *"
              value={formData.comment_text}
              onChange={(e) => { setFormData({ ...formData, comment_text: e.target.value }); if (selectedTemplate) setSelectedTemplate(null); }}
              placeholder="Enter subject comment for this student..."
            />
            <Typography variant="caption" color="text.secondary" align="right">
              {formData.comment_text.length} characters
            </Typography>
          </Box>
        </Box>

      </Box>
    </FormDialog>
  );
};

export default SubjectCommentFormDialog;
